import { useCallback, useMemo, useEffect, useState } from 'react';
import { MoneyInput } from './money-input';
import { FormInputCommonProps } from './types';
import { Listbox } from '@headlessui/react';
import { isArray } from 'lodash';
import SelectArrow from '../../images/select-arrow.svg';
import { classNames } from '../../utils';
import { CheckboxInput } from './checkbox-input';
import { RadioGroupInput } from './radio-group-input';
import { twMerge } from 'tailwind-merge';
import { get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { LabelTooltip } from './input-row';

export interface BooleanSelectOption {
  label: React.ReactNode | string;
  selectedLabel?: React.ReactNode;
  value: boolean;
  disabled?: boolean;
}

interface DropdownSelectInputProps extends FormInputCommonProps {
  multiple?: boolean;
  value?: boolean | boolean[] | undefined | null;
  options: BooleanSelectOption[];
  onChange: (value: boolean | boolean[] | undefined | null) => void;
  label: React.ReactNode;
  includeCheckboxes?: boolean;
  changeOnClose?: boolean;
  hideSelection?: boolean;
  tooltipText?: string;
  tooltipClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  labelClassName?: string;
  useCustomLabels?: boolean;
  showBorder?: boolean;
}

export const DropdownBooleanOptions: React.FC<{
  open: boolean;
  options: BooleanSelectOption[];
  disabled?: boolean;
  includeCheckboxes?: boolean;
  selectionVariant?: 'checkbox' | 'radio';
  dropdownClassName?: string;
  selectedLabel?: React.ReactNode;
}> = ({
  open,
  options,
  disabled,
  includeCheckboxes,
  selectionVariant,
  dropdownClassName,
  selectedLabel,
}) => {
  return (
    <Listbox.Options>
      <div
        className={classNames(
          'absolute z-[9999] mt-[6px] flex max-h-[286px] w-auto flex-col gap-[6px] overflow-auto rounded-[4px] bg-white p-[8px] shadow-md md:w-[256px]',
          dropdownClassName
        )}
      >
        {options.map((option, i) => (
          <Listbox.Option
            key={`${option.value.toString()}-${i}`}
            value={option.value}
            disabled={option.disabled}
            className={({ active, selected, disabled }) =>
              classNames(
                `typo-body-small text-left text-sidewalk-900 hover:bg-hardwood-100 flex h-[40px] w-full items-center p-[8px]`,
                selectedLabel === option.label ? 'bg-hardwood-50' : 'bg-white',
                active ? 'bg-hardwood-100' : '',
                disabled ? 'cursor-default opacity-40' : 'cursor-pointer'
              )
            }
          >
            {/* @ts-ignore */}
            {({ active, selected, disabled, ...rest }) => {
              return <>{option.label}</>;
            }}
          </Listbox.Option>
        ))}
      </div>
    </Listbox.Options>
  );
};

const WatchOpenStatus: React.FC<{
  open?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, onClose, children }) => {
  const [hasBeenOpen, setHasBeenOpen] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setHasBeenOpen(true);
    } else {
      if (hasBeenOpen) {
        onClose();
      }
    }
  }, [open, hasBeenOpen, setHasBeenOpen]);
  return <>{children}</>;
};

export const DropdownBooleanSelectInput: React.FC<DropdownSelectInputProps> = ({
  name,
  multiple,
  value,
  changeOnClose,
  onChange,
  options,
  label,
  includeCheckboxes = true,
  disabled,
  className,
  tooltipText,
  tooltipClassName,
  iconClassName,
  hideSelection,
  placeholder,
  inputClassName,
  dropdownClassName,
  labelClassName,
  useCustomLabels,
  showBorder = true,
}) => {
  const methods = useFormContext();
  const errors = methods?.formState.errors;
  const error = methods && name ? get(errors, name) : null;

  const [localValue, setLocalValue] = useState<
    boolean | boolean[] | undefined | null
  >(value);

  const getLabel = (localValue: boolean | boolean[] | null | undefined) => {
    if (typeof localValue === 'boolean') {
      if (useCustomLabels) {
        const selectedValue = options.find(
          (option) => option.value === localValue
        );
        return selectedValue?.label;
      } else {
        return localValue ? 'Yes' : 'No';
      }
    } else {
      return '';
    }
  };

  const selectedLabel = getLabel(localValue);

  const onClose = useCallback(() => {
    if (changeOnClose && value !== localValue) {
      onChange(localValue);
    }
  }, [localValue, onChange]);

  useEffect(() => {
    setLocalValue(value);
  }, [value, setLocalValue]);

  return (
    <div className={twMerge('block w-full', className)}>
      <Listbox
        disabled={disabled}
        value={localValue || []}
        onChange={changeOnClose ? setLocalValue : onChange}
        multiple={multiple}
      >
        {/* @ts-ignore */}
        {({ open }) => {
          return (
            <WatchOpenStatus onClose={onClose} open={open}>
              <Listbox.Button className="w-full">
                {/* @ts-ignore */}
                {(bag) => (
                  <div
                    className={twMerge(
                      'typo-body relative flex h-[40px] min-w-[126px] items-center rounded-[4px] pl-[12px] pr-[32px]  outline-none ring-0  focus:border-none focus:shadow-none focus:ring-0',
                      !!selectedLabel
                        ? 'text-sidewalk-900'
                        : 'text-sidewalk-500',
                      error
                        ? 'border-[1px] border-hydrant-500 bg-hydrant-30'
                        : showBorder
                          ? 'border-[1px] border-hardwood-300 bg-white'
                          : 'bg-white',
                      disabled
                        ? 'bg-sidewalk-30 pointer-events-none cursor-default'
                        : '',
                      inputClassName
                    )}
                  >
                    <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                      {placeholder && !selectedLabel
                        ? placeholder
                        : hideSelection
                          ? ''
                          : (selectedLabel ?? label)}
                    </div>

                    <img
                      alt="select-arrow"
                      className={classNames(
                        'absolute right-[10px] top-[50%] h-[6px] w-[10px] translate-y-[-50%] transition-all',
                        bag.open ? 'rotate-180' : ''
                      )}
                      src={SelectArrow}
                    />
                  </div>
                )}
              </Listbox.Button>
              <DropdownBooleanOptions
                open={open}
                includeCheckboxes={includeCheckboxes}
                options={options}
                disabled={disabled}
                dropdownClassName={dropdownClassName}
                selectedLabel={selectedLabel}
              />
            </WatchOpenStatus>
          );
        }}
      </Listbox>
      {name && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </div>
  );
};
